import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AuthContext } from '../../../context/AuthContext.js';
import dashboardService from '../../../services/DashboardService.js';
import { dashboardFetchFlags } from '../../../utils/dashboardAccess.js';
import { locationHubService } from '../../../services/locationHubService.js';
import {
  dashboardCacheKey,
  readDashboardCache,
  writeDashboardCache,
} from '../../../utils/dashboardCache.js';
import { buildDateParams, DEFAULT_DATE_RANGE } from './DateRangeFilter.js';
import { effectiveDashboardHubIds, hubIdsQueryParam } from '../../../utils/dashboardHubFilter.js';

const canFilterHubs = (role) => role === 'Admin' || role === 'Accountant';
const canFilterCommissionAgent = (role) =>
  role === 'Admin' || role === 'Accountant' || role === 'System Admin' || role === 'Hub Admin';

const VALID_DATE_RANGES = new Set([
  'all_time', 'this_month', 'last_3_months', 'last_6_months', 'last_year', 'custom',
]);

const emptyPrefs = {
  dateRange: DEFAULT_DATE_RANGE,
  customStart: '',
  customEnd: '',
  hubIds: [],
  cargoHubIds: [],
  bookingHubIds: [],
  shipmentHubIds: [],
  summaryDate: '',
  bookingMetric: 'net',
  commissionAgentId: '',
  onContractVehicleId: '',
  tripVehicleId: '',
  tripPartyId: '',
  attendanceUserId: '',
  attendanceHubId: '',
  salaryUserId: '',
  salaryHubId: '',
  salaryMonth: '',
  salaryYear: '',
};

const readPrefs = (user) => {
  const cached = readDashboardCache(dashboardCacheKey('ops-prefs', user));
  if (!cached || typeof cached !== 'object') return { ...emptyPrefs };
  const dateRange = VALID_DATE_RANGES.has(cached.dateRange) ? cached.dateRange : DEFAULT_DATE_RANGE;
  const customStart = cached.customStart || '';
  const customEnd = cached.customEnd || '';
  return {
    ...emptyPrefs,
    ...cached,
    dateRange: dateRange === 'custom' && (!customStart || !customEnd) ? DEFAULT_DATE_RANGE : dateRange,
    customStart,
    customEnd,
    hubIds: Array.isArray(cached.hubIds) ? cached.hubIds : [],
    cargoHubIds: Array.isArray(cached.cargoHubIds) ? cached.cargoHubIds : [],
    bookingHubIds: Array.isArray(cached.bookingHubIds) ? cached.bookingHubIds : [],
    shipmentHubIds: Array.isArray(cached.shipmentHubIds) ? cached.shipmentHubIds : [],
    bookingMetric: cached.bookingMetric || 'net',
  };
};

const hubsParam = (hubIds) => [...(hubIds || [])].map(String).sort().join(',');

export function useOperationsDashboardData({ includeUsersVehicles = false, includeMlDues = false } = {}) {
  const { user } = useContext(AuthContext);
  const fetchFlags = useMemo(
    () => dashboardFetchFlags(user, { includeUsersVehicles, includeMlDues }),
    [user, includeUsersVehicles, includeMlDues],
  );
  const showHubFilter = canFilterHubs(user?.role) && fetchFlags.hubList;
  const showCommissionAgentFilter = canFilterCommissionAgent(user?.role) && fetchFlags.commission;

  const mainKeyFor = (dateRange, customStart, customEnd, hubIds) => dashboardCacheKey('ops-main', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    uv: includeUsersVehicles ? 1 : 0,
    ml: includeMlDues ? 1 : 0,
  });
  const cargoKeyFor = (hubIds) => dashboardCacheKey('ops-cargo', user, {
    hubs: hubsParam(hubIds),
  });
  const bookingKeyFor = (hubIds, bookingMetric, summaryDate) => dashboardCacheKey('ops-booking', user, {
    hubs: hubsParam(hubIds),
    metric: bookingMetric || 'net',
    date: summaryDate || '',
  });
  const commissionKeyFor = (dateRange, customStart, customEnd, hubIds, agentId) => dashboardCacheKey('ops-commission', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    agent: agentId || '',
  });
  const onContractKeyFor = (dateRange, customStart, customEnd, vehicleId) => dashboardCacheKey('ops-oncontract', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    vehicle: vehicleId || '',
  });
  const tripKeyFor = (dateRange, customStart, customEnd, vehicleId, partyId) => dashboardCacheKey('ops-trip', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    vehicle: vehicleId || '',
    party: partyId || '',
  });
  const attendanceKeyFor = (dateRange, customStart, customEnd, hubIds, userId, hubId) => dashboardCacheKey('ops-attendance', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    hubs: hubsParam(hubIds),
    user: userId || '',
    hub: hubId || '',
  });
  const salaryKeyFor = (dateRange, customStart, customEnd, userId, hubId, month, year) => dashboardCacheKey('ops-salary', user, {
    dr: dateRange,
    cs: customStart || '',
    ce: customEnd || '',
    user: userId || '',
    hub: hubId || '',
    month: month || '',
    year: year || '',
  });

  const initialRef = useRef(null);
  if (initialRef.current == null) {
    const prefs = readPrefs(user);
    const shipmentHubs = effectiveDashboardHubIds(prefs.shipmentHubIds, prefs.hubIds);
    const cargoHubs = effectiveDashboardHubIds(prefs.cargoHubIds, prefs.hubIds);
    const bookingHubs = effectiveDashboardHubIds(prefs.bookingHubIds, prefs.hubIds);
    const main = readDashboardCache(mainKeyFor(prefs.dateRange, prefs.customStart, prefs.customEnd, shipmentHubs));
    const cargo = readDashboardCache(cargoKeyFor(cargoHubs)) || main?.cargoData || null;
    const booking = readDashboardCache(bookingKeyFor(bookingHubs, prefs.bookingMetric, prefs.summaryDate));
    const commission = readDashboardCache(commissionKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.hubIds, prefs.commissionAgentId,
    ));
    const onContract = readDashboardCache(onContractKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.onContractVehicleId,
    ));
    const trip = readDashboardCache(tripKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.tripVehicleId, prefs.tripPartyId,
    ));
    const attendance = readDashboardCache(attendanceKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd, prefs.hubIds, prefs.attendanceUserId, prefs.attendanceHubId,
    ));
    const salary = readDashboardCache(salaryKeyFor(
      prefs.dateRange, prefs.customStart, prefs.customEnd,
      prefs.salaryUserId, prefs.salaryHubId, prefs.salaryMonth, prefs.salaryYear,
    ));
    const hubs = readDashboardCache(dashboardCacheKey('ops-hubs', user));
    initialRef.current = {
      prefs,
      main,
      cargo,
      booking,
      commission,
      onContract,
      trip,
      attendance,
      salary,
      hubs,
      hasCache: Boolean(
        main?.shipmentData || cargo || booking || commission
        || onContract || trip || attendance || salary,
      ),
    };
  }
  const initial = initialRef.current;
  const hasInitialCache = initial.hasCache;

  const [loading, setLoading] = useState(!hasInitialCache);
  const [bookingLoading, setBookingLoading] = useState(true);
  const [commissionLoading, setCommissionLoading] = useState(true);
  const [dateRange, setDateRange] = useState(initial.prefs.dateRange);
  const [customStart, setCustomStart] = useState(initial.prefs.customStart);
  const [customEnd, setCustomEnd] = useState(initial.prefs.customEnd);
  const [hubIds, setHubIdsRaw] = useState(initial.prefs.hubIds);
  const [cargoHubIds, setCargoHubIds] = useState(initial.prefs.cargoHubIds);
  const [bookingHubIds, setBookingHubIds] = useState(initial.prefs.bookingHubIds);
  const [shipmentHubIds, setShipmentHubIds] = useState(initial.prefs.shipmentHubIds);
  const [hubs, setHubs] = useState(Array.isArray(initial.hubs) ? initial.hubs : []);
  const [summaryDate, setSummaryDate] = useState(initial.prefs.summaryDate);
  const [bookingMetric, setBookingMetric] = useState(initial.prefs.bookingMetric);
  const [commissionAgentId, setCommissionAgentId] = useState(initial.prefs.commissionAgentId);
  const [onContractVehicleId, setOnContractVehicleId] = useState(initial.prefs.onContractVehicleId);
  const [tripVehicleId, setTripVehicleId] = useState(initial.prefs.tripVehicleId);
  const [tripPartyId, setTripPartyId] = useState(initial.prefs.tripPartyId);
  const [attendanceUserId, setAttendanceUserId] = useState(initial.prefs.attendanceUserId);
  const [attendanceHubId, setAttendanceHubId] = useState(initial.prefs.attendanceHubId);
  const [salaryUserId, setSalaryUserId] = useState(initial.prefs.salaryUserId);
  const [salaryHubId, setSalaryHubId] = useState(initial.prefs.salaryHubId);
  const [salaryMonth, setSalaryMonth] = useState(initial.prefs.salaryMonth);
  const [salaryYear, setSalaryYear] = useState(initial.prefs.salaryYear);

  const setHubIds = (nextHubIds) => {
    setHubIdsRaw(nextHubIds);
    setCargoHubIds(nextHubIds);
    setBookingHubIds(nextHubIds);
    setShipmentHubIds(nextHubIds);
    setAttendanceHubId('');
    setSalaryHubId('');
  };

  const loadedOnce = useRef(hasInitialCache);
  const [mainRefreshing, setMainRefreshing] = useState(hasInitialCache);

  const [userData, setUserData] = useState(initial.main?.userData || null);
  const [vehicleData, setVehicleData] = useState(initial.main?.vehicleData || null);
  const [shipmentData, setShipmentData] = useState(initial.main?.shipmentData || null);
  const [commissionData, setCommissionData] = useState(initial.commission || null);
  const [onContractData, setOnContractData] = useState(initial.onContract || null);
  const [tripContractData, setTripContractData] = useState(initial.trip || null);
  const [attendanceData, setAttendanceData] = useState(initial.attendance || null);
  const [salaryData, setSalaryData] = useState(initial.salary || null);
  const [mlAgentDues, setMlAgentDues] = useState(initial.main?.mlAgentDues || null);
  const [cargoData, setCargoData] = useState(initial.cargo || initial.main?.cargoData || null);
  const [bookingSummary, setBookingSummary] = useState(initial.booking || null);
  const [cargoLoading, setCargoLoading] = useState(true);
  const [onContractLoading, setOnContractLoading] = useState(true);
  const [tripLoading, setTripLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [salaryLoading, setSalaryLoading] = useState(true);

  useEffect(() => {
    writeDashboardCache(dashboardCacheKey('ops-prefs', user), {
      dateRange, customStart, customEnd, hubIds,
      cargoHubIds, bookingHubIds, shipmentHubIds,
      summaryDate, bookingMetric,
      commissionAgentId, onContractVehicleId, tripVehicleId, tripPartyId,
      attendanceUserId, attendanceHubId, salaryUserId, salaryHubId, salaryMonth, salaryYear,
    });
  }, [
    user?.user_id, user?.company_id, dateRange, customStart, customEnd, hubIds,
    cargoHubIds, bookingHubIds, shipmentHubIds,
    summaryDate, bookingMetric, commissionAgentId, onContractVehicleId, tripVehicleId, tripPartyId,
    attendanceUserId, attendanceHubId, salaryUserId, salaryHubId, salaryMonth, salaryYear,
  ]);

  useEffect(() => {
    if (!fetchFlags.hubList) {
      setHubs([]);
      return undefined;
    }
    locationHubService.getHubsForSelect()
      .then((res) => {
        const next = res.data?.hubs || res.data?.data || [];
        setHubs(next);
        writeDashboardCache(dashboardCacheKey('ops-hubs', user), next);
      })
      .catch(() => {});
    return undefined;
  }, [fetchFlags.hubList, user?.user_id, user?.company_id]);

  useEffect(() => {
    if (!fetchFlags.shipment) {
      setShipmentData(null);
      setUserData(null);
      setVehicleData(null);
      setMlAgentDues(null);
      setLoading(false);
      setMainRefreshing(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const shipmentHubs = effectiveDashboardHubIds(shipmentHubIds, hubIds);
    const cacheKey = mainKeyFor(dateRange, customStart, customEnd, shipmentHubs);
    const cached = readDashboardCache(cacheKey);
    if (cached) {
      if (cached.shipmentData) setShipmentData(cached.shipmentData);
      if (cached.userData) setUserData(cached.userData);
      if (cached.vehicleData) setVehicleData(cached.vehicleData);
      if (cached.mlAgentDues) setMlAgentDues(cached.mlAgentDues);
      setLoading(false);
      loadedOnce.current = true;
      setMainRefreshing(true);
    } else if (!loadedOnce.current) {
      setLoading(true);
    } else {
      setShipmentData(null);
      setMainRefreshing(true);
    }
    let cancelled = false;
    const fetchMain = async () => {
      try {
        const hubParams = hubIdsQueryParam(showHubFilter, shipmentHubs);
        const dp = { ...buildDateParams(dateRange, customStart, customEnd), limit: 5, ...hubParams };
        const requests = [
          dashboardService.getShipments(dp).catch(() => null),
        ];
        if (fetchFlags.usersVehicles) {
          requests.push(dashboardService.getUsers().catch(() => null));
          requests.push(dashboardService.getVehicles().catch(() => null));
        }
        if (fetchFlags.mlAgentDues) {
          requests.push(dashboardService.getMlAgentDues(dp).catch(() => null));
        }
        const results = await Promise.all(requests);
        if (cancelled) return;
        const next = { ...(cached || {}) };
        if (results[0]?.data) {
          next.shipmentData = results[0].data;
          setShipmentData(results[0].data);
        }
        let idx = 1;
        if (fetchFlags.usersVehicles) {
          if (results[idx]?.data) {
            next.userData = results[idx].data;
            setUserData(results[idx].data);
          }
          if (results[idx + 1]?.data) {
            next.vehicleData = results[idx + 1].data;
            setVehicleData(results[idx + 1].data);
          }
          idx += 2;
        }
        if (fetchFlags.mlAgentDues && results[idx]?.data) {
          next.mlAgentDues = results[idx].data;
          setMlAgentDues(results[idx].data);
        }
        writeDashboardCache(cacheKey, next);
        loadedOnce.current = true;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setMainRefreshing(false);
        }
      }
    };
    fetchMain();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, hubIds, shipmentHubIds, showHubFilter,
    fetchFlags.shipment, fetchFlags.usersVehicles, fetchFlags.mlAgentDues,
    user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.cargoOverview) {
      setCargoData(null);
      setCargoLoading(false);
      return undefined;
    }
    const cargoHubs = effectiveDashboardHubIds(cargoHubIds, hubIds);
    const cacheKey = cargoKeyFor(cargoHubs);
    const cached = readDashboardCache(cacheKey);
    if (cached) setCargoData(cached);
    else setCargoData(null);
    let cancelled = false;
    const fetchCargo = async () => {
      try {
        setCargoLoading(true);
        const hubParams = hubIdsQueryParam(showHubFilter, cargoHubs);
        const res = await dashboardService.getCargoOverview(hubParams).catch(() => null);
        if (!cancelled && res?.data) {
          setCargoData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setCargoLoading(false);
      }
    };
    fetchCargo();
    return () => { cancelled = true; };
  }, [hubIds, cargoHubIds, showHubFilter, fetchFlags.cargoOverview, user?.user_id, user?.company_id]);

  useEffect(() => {
    if (!fetchFlags.bookingSummary) {
      setBookingSummary(null);
      setBookingLoading(false);
      return undefined;
    }
    const bookingHubs = effectiveDashboardHubIds(bookingHubIds, hubIds);
    const cacheKey = bookingKeyFor(bookingHubs, bookingMetric, summaryDate);
    const cached = readDashboardCache(cacheKey);
    if (cached) setBookingSummary(cached);
    else setBookingSummary(null);
    let cancelled = false;
    const fetchBooking = async () => {
      try {
        setBookingLoading(true);
        const hubParams = hubIdsQueryParam(showHubFilter, bookingHubs);
        const bookingParams = {
          ...hubParams,
          metric: bookingMetric,
          ...(summaryDate ? { summary_date: summaryDate } : {}),
        };
        const res = await dashboardService.getBookingSummary(bookingParams).catch(() => null);
        if (!cancelled && res?.data) {
          setBookingSummary(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setBookingLoading(false);
      }
    };
    fetchBooking();
    return () => { cancelled = true; };
  }, [
    hubIds, bookingHubIds, showHubFilter, bookingMetric, summaryDate,
    fetchFlags.bookingSummary, user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.commission) {
      setCommissionData(null);
      setCommissionLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = commissionKeyFor(dateRange, customStart, customEnd, hubIds, commissionAgentId);
    const cached = readDashboardCache(cacheKey);
    if (cached) setCommissionData(cached);
    else setCommissionData(null);
    let cancelled = false;
    const fetchCommissions = async () => {
      try {
        setCommissionLoading(true);
        const hubParams = hubIdsQueryParam(showHubFilter, hubIds);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 20,
          ...hubParams,
          ...(showCommissionAgentFilter && commissionAgentId
            ? { agent_user_id: commissionAgentId }
            : {}),
        };
        const res = await dashboardService.getCommissions(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setCommissionData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setCommissionLoading(false);
      }
    };
    fetchCommissions();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, hubIds, showHubFilter,
    showCommissionAgentFilter, commissionAgentId, fetchFlags.commission,
    user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.onContract) {
      setOnContractData(null);
      setOnContractLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = onContractKeyFor(dateRange, customStart, customEnd, onContractVehicleId);
    const cached = readDashboardCache(cacheKey);
    if (cached) setOnContractData(cached);
    else setOnContractData(null);
    let cancelled = false;
    const fetchOnContracts = async () => {
      try {
        setOnContractLoading(true);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...(onContractVehicleId ? { vehicle_id: onContractVehicleId } : {}),
        };
        const res = await dashboardService.getOnContracts(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setOnContractData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setOnContractLoading(false);
      }
    };
    fetchOnContracts();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, onContractVehicleId, fetchFlags.onContract,
    user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.tripContract) {
      setTripContractData(null);
      setTripLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = tripKeyFor(dateRange, customStart, customEnd, tripVehicleId, tripPartyId);
    const cached = readDashboardCache(cacheKey);
    if (cached) setTripContractData(cached);
    else setTripContractData(null);
    let cancelled = false;
    const fetchTripContracts = async () => {
      try {
        setTripLoading(true);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...(tripVehicleId ? { vehicle_id: tripVehicleId } : {}),
          ...(tripPartyId ? { contract_party_id: tripPartyId } : {}),
        };
        const res = await dashboardService.getTripContracts(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setTripContractData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setTripLoading(false);
      }
    };
    fetchTripContracts();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, tripVehicleId, tripPartyId, fetchFlags.tripContract,
    user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.attendance) {
      setAttendanceData(null);
      setAttendanceLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = attendanceKeyFor(
      dateRange, customStart, customEnd, hubIds, attendanceUserId, attendanceHubId,
    );
    const cached = readDashboardCache(cacheKey);
    if (cached) setAttendanceData(cached);
    else setAttendanceData(null);
    let cancelled = false;
    const fetchAttendance = async () => {
      try {
        setAttendanceLoading(true);
        const hubParams = hubIdsQueryParam(showHubFilter, hubIds);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...hubParams,
          ...(attendanceUserId ? { user_id: attendanceUserId } : {}),
          ...(attendanceHubId ? { location_hub_id: attendanceHubId } : {}),
        };
        const res = await dashboardService.getAttendance(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setAttendanceData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setAttendanceLoading(false);
      }
    };
    fetchAttendance();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd, hubIds, showHubFilter,
    attendanceUserId, attendanceHubId, fetchFlags.attendance,
    user?.user_id, user?.company_id,
  ]);

  useEffect(() => {
    if (!fetchFlags.salary) {
      setSalaryData(null);
      setSalaryLoading(false);
      return undefined;
    }
    if (dateRange === 'custom' && (!customStart || !customEnd)) return undefined;
    const cacheKey = salaryKeyFor(
      dateRange, customStart, customEnd, salaryUserId, salaryHubId, salaryMonth, salaryYear,
    );
    const cached = readDashboardCache(cacheKey);
    if (cached) setSalaryData(cached);
    else setSalaryData(null);
    let cancelled = false;
    const fetchSalaries = async () => {
      try {
        setSalaryLoading(true);
        const dp = {
          ...buildDateParams(dateRange, customStart, customEnd),
          limit: 5,
          ...(salaryUserId ? { user_id: salaryUserId } : {}),
          ...(salaryHubId ? { location_hub_id: salaryHubId } : {}),
          ...(salaryMonth ? { month: salaryMonth } : {}),
          ...(salaryYear ? { year: salaryYear } : {}),
        };
        const res = await dashboardService.getSalaries(dp).catch(() => null);
        if (!cancelled && res?.data) {
          setSalaryData(res.data);
          writeDashboardCache(cacheKey, res.data);
        }
      } finally {
        if (!cancelled) setSalaryLoading(false);
      }
    };
    fetchSalaries();
    return () => { cancelled = true; };
  }, [
    dateRange, customStart, customEnd,
    salaryUserId, salaryHubId, salaryMonth, salaryYear, fetchFlags.salary,
    user?.user_id, user?.company_id,
  ]);

  const refreshing = Boolean(
    (fetchFlags.shipment && mainRefreshing)
    || (fetchFlags.cargoOverview && cargoLoading)
    || (fetchFlags.bookingSummary && bookingLoading)
    || (fetchFlags.commission && commissionLoading)
    || (fetchFlags.onContract && onContractLoading)
    || (fetchFlags.tripContract && tripLoading)
    || (fetchFlags.attendance && attendanceLoading)
    || (fetchFlags.salary && salaryLoading)
    || (fetchFlags.shipment && loading && hasInitialCache),
  );

  return {
    user,
    loading,
    refreshing,
    mainRefreshing,
    cargoLoading,
    bookingLoading,
    commissionLoading,
    showHubFilter,
    showCommissionAgentFilter,
    dateRange, setDateRange, customStart, setCustomStart, customEnd, setCustomEnd,
    hubIds, setHubIds, hubs,
    cargoHubIds, setCargoHubIds,
    bookingHubIds, setBookingHubIds,
    shipmentHubIds, setShipmentHubIds,
    summaryDate, setSummaryDate, bookingMetric, setBookingMetric,
    commissionAgentId, setCommissionAgentId,
    onContractVehicleId, setOnContractVehicleId,
    tripVehicleId, setTripVehicleId,
    tripPartyId, setTripPartyId,
    attendanceUserId, setAttendanceUserId,
    attendanceHubId, setAttendanceHubId,
    salaryUserId, setSalaryUserId,
    salaryHubId, setSalaryHubId,
    salaryMonth, setSalaryMonth,
    salaryYear, setSalaryYear,
    onContractLoading, tripLoading, attendanceLoading, salaryLoading,
    userData, vehicleData, shipmentData, commissionData,
    onContractData, tripContractData, attendanceData, salaryData,
    mlAgentDues, cargoData, bookingSummary,
    fetchFlags,
  };
}
